import { SBBox, SBLoading } from "modules/Commons";
import SBEmptyState from "modules/Commons/SBLayouts/SBEmptyState";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouteFilters } from "hooks/useRouteFilters";
import { IComposedFilter } from "domain/Utils";
import { useSystemFilterContext } from "contexts/SystemFilterContext";
import { ISupplyOrder } from "domain/Inventory/Models/ISupplyOrder";
import { ISupplyOrderSummary } from "domain/Inventory/Models/ISupplyOrderSummary";
import SBComposedFilter from "views/Commons/SBComposedFilter";
import { useSupplyOrderRequester } from "modules/SupplyOrder/hooks/useSupplyOrderRequester";
import SupplyOrderTable from "modules/SupplyOrder/components/SupplyOrderTable";
import SupplyOrderEmptyState from "modules/SupplyOrder/components/SupplyOrderEmptyState";
import SupplyOrderUploaderHelper from "modules/SupplyOrder/components/SupplyOrderUploaderHelper";
import { useStyles } from "./style";

const DEFAULT_PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 600;

export default () => {
	const [items, setItems] = useState<ISupplyOrder[]>([]);
	const [loading, setLoading] = useState(false);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [skip, setSkip] = useState(0);
	const [isImportModalOpen, setIsImportModalOpen] = useState(false);
	const [summary, setSummary] = useState<ISupplyOrderSummary | undefined>();
	const classes = useStyles();
	const tableRef = useRef<any>();
	const { t } = useTranslation();

	const requester = useSupplyOrderRequester();
	const { context, filtersContext, setFiltersContextState } = useSystemFilterContext();
	const { queryFilters } = useRouteFilters();
	const activeFilterRef = useRef<IComposedFilter | undefined>();

	const hasItems = items.length > 0;

	const fetchItems = (currentSkip = 0, append = false) => {
		if (append) setIsFetchingMore(true);
		else setLoading(true);

		requester
			.getAll(currentSkip, DEFAULT_PAGE_SIZE, activeFilterRef.current, false)
			.then((result) => {
				setItems((prev) => append ? [...prev, ...result] : result);
				setSkip(currentSkip + result.length);
				setHasMore(result.length === DEFAULT_PAGE_SIZE);
			})
			.finally(() => {
				setLoading(false);
				setIsFetchingMore(false);
			});
	};

	const fetchCount = (filter?: IComposedFilter) => {
		requester.getCount(filter, false).then(setSummary);
	};

	const handleOnApply = (obj: IComposedFilter) => {
		if (obj.filter) setFiltersContextState(obj.filter);
		else setFiltersContextState([]);
		activeFilterRef.current = obj;
		fetchItems(0);
		fetchCount(obj);
	};

	useEffect(() => {
		if (context)
			handleOnApply({
				filter: filtersContext?.items,
				picker: {
					range: {
						startDate: queryFilters.start,
						endDate: queryFilters.end,
					},
				},
			} as IComposedFilter);
	}, [context]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
		if (
			!isFetchingMore &&
			hasMore &&
			scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD
		) {
			fetchItems(skip, true);
		}
	};

	const handleExport = () => {
		tableRef.current?.downloadCSV();
	};

	return (
		<SBBox className={classes.mainBox}>
			<SBBox display="flex" justifyContent="end">
				<SBComposedFilter.Base onApply={handleOnApply}>
					<SBComposedFilter.Picker />
					<SBComposedFilter.Filter />
					<SBComposedFilter.Applicator />
					<SBComposedFilter.Actions
						actions={[
							{
								icon: { name: "upload" },
								title: "uploadCSV",
								onClick: () => setIsImportModalOpen(true),
							},
							{
								icon: { name: "download" },
								title: "exportCSV",
								onClick: handleExport,
							},
						]}
					/>
				</SBComposedFilter.Base>
			</SBBox>

			<SBBox mt={2}>
				<SBLoading loading={loading}>
					<SBEmptyState show={hasItems} emptyState={<SupplyOrderEmptyState />}>
						<SupplyOrderTable
							ref={tableRef}
							title={t("supplyOrderPendingTableTitle")}
							rows={items}
							summary={summary}
							containerHeight={312}
							onScroll={handleScroll}
						/>
					</SBEmptyState>
				</SBLoading>
			</SBBox>

			<SupplyOrderUploaderHelper
				open={isImportModalOpen}
				handleClose={() => setIsImportModalOpen(false)}
				onSave={() => fetchItems(0)}
			/>
		</SBBox>
	);
};
